'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useState } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import type { ClothingItem } from '@/types/closet';
import { categoryConfig } from '@/data/closet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useClosetAuth } from '@/contexts/ClosetAuthContext';
import { CardDeleteButton } from './CardDeleteButton';
import { Shimmer } from './Shimmer';
interface ClothingCardProps {
  item: ClothingItem;
  index?: number;
  isDimmed?: boolean;
  onSelect?: () => void;
  isSelected?: boolean;
  onHover?: () => void;
  onDelete?: () => void;
  isProcessing?: boolean;
}
function cardClassName(selectable: boolean, isSelected: boolean, isDimmed: boolean): string {
  return `content-visibility-auto group ease-out-strong relative aspect-square overflow-hidden rounded-lg transition-[opacity,transform] duration-200 ${selectable ? 'cursor-pointer active:scale-[0.98]' : ''} ${isSelected ? 'ring-2 ring-zinc-900 dark:ring-white' : ''} ${isDimmed ? 'opacity-20' : 'opacity-100'}`;
}
interface CardMediaProps {
  item: ClothingItem;
  index?: number;
  imageLoaded: boolean;
  imageError: boolean;
  onLoad: () => void;
  onError: () => void;
}
function CardMedia(t0) {
  const $ = _c(15);
  const {
    item,
    index,
    imageLoaded,
    imageError,
    onLoad,
    onError
  } = t0;
  const hasImage = !imageError && Boolean(item.imageUrl) && item.imageUrl !== "";
  let t1;
  if ($[0] !== hasImage || $[1] !== imageLoaded) {
    t1 = !imageLoaded && hasImage && <div className="absolute inset-0 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800" />;
    $[0] = hasImage;
    $[1] = imageLoaded;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hasImage || $[4] !== imageLoaded || $[5] !== index || $[6] !== item.category || $[7] !== item.imageUrl || $[8] !== item.name || $[9] !== onError || $[10] !== onLoad) {
    t2 = hasImage ? <Image src={item.imageUrl} alt={item.name} fill={true} sizes="(max-width: 768px) 150px, 200px" priority={index !== undefined && index < 8} onLoad={onLoad} onError={onError} className={`object-contain transition-opacity duration-300 ${imageLoaded ? "opacity-100" : "opacity-0"}`} /> : <div className="flex h-full items-center justify-center"><span className="text-xs text-zinc-400">{categoryConfig[item.category].label.toUpperCase()}</span></div>;
    $[3] = hasImage;
    $[4] = imageLoaded;
    $[5] = index;
    $[6] = item.category;
    $[7] = item.imageUrl;
    $[8] = item.name;
    $[9] = onError;
    $[10] = onLoad;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = <div className="relative aspect-square">{t1}{t2}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
interface CardOverlayProps {
  item: ClothingItem;
  showOverlay: boolean;
  isProcessing: boolean;
}
function CardOverlay(t0) {
  const $ = _c(6);
  const {
    item,
    showOverlay,
    isProcessing
  } = t0;
  const t1 = `absolute inset-0 flex flex-col items-center justify-center bg-neutral-950/60 p-4 transition-opacity duration-200 ${showOverlay || isProcessing ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`;
  let t2;
  if ($[0] !== isProcessing || $[1] !== item) {
    t2 = isProcessing ? <><Shimmer /><p className="text-xs text-white/80">APPLYING…</p></> : <><h3 className="text-center text-sm font-medium text-white">{item.name.toUpperCase()}</h3>{item.brand && <p className="mt-1 text-center text-xs text-white/80">{item.brand.toUpperCase()}</p>}</>;
    $[0] = isProcessing;
    $[1] = item;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function ClothingCard(t0) {
  const $ = _c(44);
  const {
    item,
    index,
    isDimmed: t1,
    onSelect,
    isSelected: t2,
    onHover,
    onDelete,
    isProcessing: t3
  } = t0;
  const isDimmed = t1 === undefined ? false : t1;
  const isSelected = t2 === undefined ? false : t2;
  const isProcessing = t3 === undefined ? false : t3;
  const haptic = useWebHaptics();
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const {
    isAuthenticated
  } = useClosetAuth();
  const isMobile = useIsMobile();
  let t4;
  if ($[0] !== isMobile || $[1] !== onSelect) {
    t4 = () => {
      if (isMobile) {
        setShowOverlay(_temp);
      }
      onSelect?.();
    };
    $[0] = isMobile;
    $[1] = onSelect;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const handleCardPress = t4;
  let t5;
  if ($[3] !== isMobile || $[4] !== onHover) {
    t5 = () => {
      onHover?.();
      if (!isMobile) {
        setShowOverlay(true);
      }
    };
    $[3] = isMobile;
    $[4] = onHover;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[6] !== isMobile) {
    t6 = () => {
      if (!isMobile) {
        setShowOverlay(false);
      }
    };
    $[6] = isMobile;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleMouseLeave = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setImageLoaded(true);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleImageLoad = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setImageError(true);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleImageError = t8;
  let t9;
  if ($[10] !== haptic || $[11] !== onDelete) {
    t9 = () => {
      haptic.trigger("light");
      onDelete?.();
    };
    $[10] = haptic;
    $[11] = onDelete;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleDeleteClick = t9;
  const t10 = Boolean(onSelect);
  let t11;
  if ($[13] !== isDimmed || $[14] !== isSelected || $[15] !== t10) {
    t11 = cardClassName(t10, isSelected, isDimmed);
    $[13] = isDimmed;
    $[14] = isSelected;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = `View ${item.name}`;
  let t13;
  if ($[17] !== handleCardPress || $[18] !== isSelected || $[19] !== t12) {
    t13 = <button type="button" onClick={handleCardPress} aria-label={t12} aria-pressed={isSelected} className="absolute inset-0 z-10 cursor-[inherit]" />;
    $[17] = handleCardPress;
    $[18] = isSelected;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== imageError || $[22] !== imageLoaded || $[23] !== index || $[24] !== item) {
    t14 = <CardMedia item={item} index={index} imageLoaded={imageLoaded} imageError={imageError} onLoad={handleImageLoad} onError={handleImageError} />;
    $[21] = imageError;
    $[22] = imageLoaded;
    $[23] = index;
    $[24] = item;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== isProcessing || $[27] !== item || $[28] !== showOverlay) {
    t15 = <CardOverlay item={item} showOverlay={showOverlay} isProcessing={isProcessing} />;
    $[26] = isProcessing;
    $[27] = item;
    $[28] = showOverlay;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== handleDeleteClick || $[31] !== isAuthenticated || $[32] !== isProcessing || $[33] !== onDelete || $[34] !== showOverlay) {
    t16 = isAuthenticated && onDelete && !isProcessing && <CardDeleteButton visible={showOverlay} label="Delete item" onClick={handleDeleteClick} />;
    $[30] = handleDeleteClick;
    $[31] = isAuthenticated;
    $[32] = isProcessing;
    $[33] = onDelete;
    $[34] = showOverlay;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== handleMouseEnter || $[37] !== handleMouseLeave || $[38] !== t11 || $[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
    t17 = <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t11}>{t13}{t14}{t15}{t16}</div>;
    $[36] = handleMouseEnter;
    $[37] = handleMouseLeave;
    $[38] = t11;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
}
function _temp(prev) {
  return !prev;
}